import { useEffect, useRef, useCallback } from "react";
import { toast } from "@altimateai/lego";
import { isTerminalStatus, isFailedStatus } from "../utils/statusHelpers";

interface UseDocumentStatusNotificationOptions {
  status?: string | null;
  documentName?: string;
}

/**
 * Hook to show toast notifications when document processing completes.
 * If the browser tab is not visible, the notification persists until the user returns.
 */
export function useDocumentStatusNotification({
  status,
  documentName,
}: UseDocumentStatusNotificationOptions) {
  const previousStatusRef = useRef<string | null | undefined>(status);
  const pendingToastRef = useRef<{ dismiss: () => void } | null>(null);

  // Handle tab visibility change - dismiss persistent toast when tab becomes visible
  const handleVisibilityChange = useCallback(() => {
    if (!document.hidden && pendingToastRef.current) {
      // Tab is now visible, dismiss the persistent toast after showing briefly
      const toastRef = pendingToastRef.current;
      pendingToastRef.current = null;

      // Let the user see the notification for 5 seconds before dismissing
      setTimeout(() => {
        toastRef.dismiss();
      }, 5000);
    }
  }, []);

  // Set up visibility change listener
  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  // Detect status transitions and show notifications
  useEffect(() => {
    const previousStatus = previousStatusRef.current;

    // Update ref for next comparison
    previousStatusRef.current = status;

    // Only notify if transitioning from non-terminal to terminal
    if (!status || !previousStatus) return;
    if (isTerminalStatus(previousStatus)) return; // Was already terminal
    if (!isTerminalStatus(status)) return; // Still not terminal

    const isFailed = isFailedStatus(status);
    const title = isFailed ? "Document processing failed" : "Document processing complete";
    const description = documentName
      ? isFailed
        ? `"${documentName}" failed to process. You can retry from the History tab.`
        : `"${documentName}" has been processed successfully.`
      : isFailed
        ? "Document failed to process. You can retry from the History tab."
        : "Document has been processed successfully.";

    // If tab is hidden, show persistent toast
    if (document.hidden) {
      const toastResult = toast({
        title,
        description,
        variant: isFailed ? "destructive" : "default",
        duration: Infinity, // Persist until tab is visible
      });
      pendingToastRef.current = toastResult;
    } else {
      // Tab is visible, show normal toast
      toast({
        title,
        description,
        variant: isFailed ? "destructive" : "default",
      });
    }
  }, [status, documentName]);
}
